import { useEffect, useState } from "react";
import { Col, Form, Nav } from "react-bootstrap";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

interface Article {
  slug: string;
  title: string;
  sortOrder: number;
}

interface Section {
  section: string;
  sectionTitle: string;
  sectionOrder: number;
  articles: Article[];
}

interface SearchResult {
  id: number;
  section: string;
  slug: string;
  title: string;
  headline: string;
}

export default function HelpNav({
  contextArticle,
}: {
  contextArticle?: string;
}) {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [sections, setSections] = useState<Section[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  // The answer of the search for one text; none for the current text means it waits.
  const [searchAnswer, setSearchAnswer] = useState<{
    query: string;
    results: SearchResult[] | null;
  } | null>(null);
  const [sectionsFailed, setSectionsFailed] = useState(false);
  const [navigated, setNavigated] = useState(false);

  useEffect(() => {
    async function loadSections() {
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${process.env.REACT_APP_API_ROOT}/help/sections`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!response.ok) {
          setSectionsFailed(true);
          return;
        }
        const data = await response.json();
        setSections(data.data);
      } catch {
        setSectionsFailed(true);
      }
    }
    loadSections();
  }, [getAccessTokenSilently]);

  // Navigate to context article on first load
  useEffect(() => {
    if (contextArticle && !navigated && sections.length > 0) {
      setNavigated(true);
      navigate(`/help/${contextArticle}`, { replace: true });
    }
  }, [contextArticle, navigated, sections, navigate]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchAnswer(null);
      return;
    }

    let cancelled = false;
    const answer = (results: SearchResult[] | null) => {
      if (!cancelled) setSearchAnswer({ query: searchQuery, results });
    };
    const timer = setTimeout(async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${process.env.REACT_APP_API_ROOT}/help/search?q=${encodeURIComponent(searchQuery)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!response.ok) {
          answer(null);
          return;
        }
        const data = await response.json();
        answer(data.data);
      } catch {
        answer(null);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchQuery, getAccessTokenSilently]);

  const searching = searchQuery.trim() !== "";
  const current = searchAnswer?.query === searchQuery ? searchAnswer : null;
  const searchResults = current?.results ?? [];

  return (
    <Col md={3} lg={2} className="help-sidebar py-3">
      <Form.Control
        type="search"
        placeholder="Search help..."
        size="sm"
        className="mb-3"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />

      {searching && !current ? (
        <p className="px-3 text-muted small" role="status">
          Searching…
        </p>
      ) : searching && current?.results === null ? (
        <p className="px-3 text-danger small" role="alert">
          The search did not load
        </p>
      ) : searching && searchResults.length > 0 ? (
        <Nav className="flex-column">
          <h6 className="px-3 text-uppercase small fw-bold">Search Results</h6>
          {searchResults.map((result) => (
            <Nav.Link
              key={result.id}
              as={NavLink}
              to={`/help/${result.section}/${result.slug}`}
              className="py-1 px-3"
              onClick={() => setSearchQuery("")}
            >
              {result.title}
            </Nav.Link>
          ))}
        </Nav>
      ) : searchQuery.trim() && searchResults.length === 0 ? (
        <p className="px-3 text-muted small">No results found.</p>
      ) : sectionsFailed ? (
        <p className="px-3 text-muted small" role="alert">
          Help sections did not load. Try again later.
        </p>
      ) : (
        <Nav className="flex-column">
          {sections.map((section) => (
            <div key={section.section} className="mb-3">
              <h6 className="px-3 pt-2 text-uppercase small fw-bold">
                {section.sectionTitle}
              </h6>
              {section.articles.map((article) => (
                <Nav.Link
                  key={article.slug}
                  as={NavLink}
                  to={`/help/${section.section}/${article.slug}`}
                  className="py-1 px-3"
                >
                  {article.title}
                </Nav.Link>
              ))}
            </div>
          ))}
        </Nav>
      )}
    </Col>
  );
}
